
import { useState, useEffect } from "react"
import { Bookmark, Trash2, Loader2 } from "lucide-react"
import { PostCard } from "@/components/posts/PostCard"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { useAuth } from "@/context/AuthContext"
import { getBookmarkedPosts, removeBookmark } from "@/lib/db"
import type { PostWithAuthor } from "@/types"

export default function SavedPostsPage() {
  const { firebaseUser } = useAuth()
  const [savedPosts, setSavedPosts] = useState<PostWithAuthor[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!firebaseUser) return

    async function loadBookmarks() {
      try {
        const posts = await getBookmarkedPosts(firebaseUser!.id)
        setSavedPosts(posts)
      } catch (error) {
        console.error("Failed to load bookmarks:", error)
      } finally {
        setLoading(false)
      }
    }

    loadBookmarks()
  }, [firebaseUser])

  const handleRemove = async (postId: string) => {
    if (!firebaseUser) return
    try {
      await removeBookmark(firebaseUser.id, postId)
      setSavedPosts(prev => prev.filter(p => p.id !== postId))
    } catch (error) {
      console.error("Failed to remove bookmark:", error)
    }
  }

  const handleClearAll = async () => {
    if (!firebaseUser) return
    try {
      await Promise.all(savedPosts.map(p => removeBookmark(firebaseUser.id, p.id)))
      setSavedPosts([])
    } catch (error) {
      console.error("Failed to clear bookmarks:", error)
    }
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Saved Posts</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {savedPosts.length} {savedPosts.length === 1 ? "post" : "posts"} saved
          </p>
        </div>
        {savedPosts.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Trash2 className="mr-2 h-4 w-4" />
                Clear All
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear All Saved Posts</AlertDialogTitle>
                <AlertDialogDescription>Are you sure you want to remove all saved posts? This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearAll}>Clear All</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {savedPosts.length > 0 ? (
        <div className="space-y-6">
          {savedPosts.map(post => (
            <div key={post.id} className="relative">
              <PostCard post={post} />
              <Button variant="ghost" size="sm" className="absolute right-2 top-2" onClick={() => handleRemove(post.id)}>
                <Bookmark className="h-4 w-4 fill-current" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-12 text-center">
          <Bookmark className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No saved posts</h3>
          <p className="mt-2 text-gray-500 dark:text-gray-400">Posts you save will appear here for easy access.</p>
        </div>
      )}
    </div>
  )
}
